import {
  create,
  type Mutate,
  type StoreApi,
  type UseBoundStore,
} from "zustand";
import {
  createJSONStorage,
  persist,
  type StateStorage,
} from "zustand/middleware";
import {
  findMixLineupEntry,
  type MixShare,
  type MixSharePercent,
} from "@/config/mixLineup";

export const MIX_STORE_VERSION = 3;

/** 総合カタログで選べる取り分(%)の選択肢。「制御なし」はこれとは別枠。 */
export const MIX_SHARE_PERCENTS: readonly MixSharePercent[] = [5, 10, 20, 30];

export type MixBoardSetting = Readonly<{
  enabled: boolean;
  share: MixShare;
}>;

export type MixBoardSettings = Readonly<Record<string, MixBoardSetting>>;

/**
 * 設定を触っていないslugの初期値。全板を平等に扱うため取り分は常に
 * 「制御なし」で始め、ON/OFFだけをラインナップ定義から引く。
 */
export function defaultMixBoardSetting(slug: string): MixBoardSetting {
  return Object.freeze({
    enabled: findMixLineupEntry(slug)?.defaultEnabled ?? false,
    share: "uncapped" as const,
  });
}

type MixExtra = Readonly<{ slug: string; pct: MixSharePercent }>;

/**
 * 総合カタログ1ページの構成。uncappedSlugsはサーバー(catalog-mix)がbump順を
 * 維持したまま混合する板群、extrasは%取り分の上限付きで混ざる板。
 * 全板に%上限を付けた構成ではuncappedSlugsが空になり、ページは
 * 取り分の合計までしか埋まらない。
 */
export type MixSelection = Readonly<{
  uncappedSlugs: readonly string[];
  extras: readonly MixExtra[];
}>;

/**
 * 取り分とは無関係に、総合ページで表示対象になっている板を返す。
 * 永続値が壊れて全板OFFになった場合は、カタログと同じく先頭板へ戻す。
 */
export function resolveEnabledMixSlugs(
  registeredSlugs: readonly string[],
  boardSettings: MixBoardSettings,
): readonly string[] {
  const enabledSlugs = registeredSlugs.filter(
    (slug) => (boardSettings[slug] ?? defaultMixBoardSetting(slug)).enabled,
  );
  if (enabledSlugs.length > 0) return Object.freeze(enabledSlugs);
  const [firstSlug] = registeredSlugs;
  return firstSlug === undefined ? [] : Object.freeze([firstSlug]);
}

/**
 * 登録済み総合カタログ対象とユーザー設定から実際に混ぜる構成を決める。
 * どの板も特別扱いせず、ONの板だけを取り分の指定どおりに振り分ける。
 * 設定の手編集や板の登録解除でONが1板も無くなった場合だけ、
 * 空のカタログにしないため登録済み先頭板を制御なしとして採用する。
 */
export function resolveMixSelection(
  registeredSlugs: readonly string[],
  boardSettings: MixBoardSettings,
): MixSelection | null {
  const [firstSlug] = registeredSlugs;
  if (firstSlug === undefined) return null;

  const uncappedSlugs: string[] = [];
  const extras: MixExtra[] = [];
  for (const slug of registeredSlugs) {
    const setting = boardSettings[slug] ?? defaultMixBoardSetting(slug);
    if (!setting.enabled) continue;
    if (setting.share === "uncapped") {
      uncappedSlugs.push(slug);
    } else {
      extras.push(Object.freeze({ slug, pct: setting.share }));
    }
  }
  if (uncappedSlugs.length === 0 && extras.length === 0) {
    uncappedSlugs.push(firstSlug);
  }
  return Object.freeze({
    uncappedSlugs: Object.freeze(uncappedSlugs),
    extras: Object.freeze(extras),
  });
}

interface MixValues {
  boardSettings: MixBoardSettings;
}

interface MixActions {
  setBoardEnabled: (slug: string, enabled: boolean) => void;
  setBoardShare: (slug: string, share: MixShare) => void;
  resetBoardSettings: () => void;
}

type MixState = MixValues & MixActions;
export type MixStore = UseBoundStore<
  Mutate<StoreApi<MixState>, [["zustand/persist", unknown]]>
>;

const DEFAULT_MIX_VALUES: MixValues = {
  boardSettings: {},
};

function updateBoardSetting(
  boardSettings: MixBoardSettings,
  slug: string,
  patch: Partial<MixBoardSetting>,
): MixBoardSettings {
  const current = boardSettings[slug] ?? defaultMixBoardSetting(slug);
  return {
    ...boardSettings,
    [slug]: Object.freeze({ ...current, ...patch }),
  };
}

export function createMixStore(storage?: StateStorage): MixStore {
  const persistStorage = resolveMixStateStorage(storage);
  const jsonStorage = persistStorage
    ? createJSONStorage(() => persistStorage)
    : undefined;

  return create<MixState>()(
    persist(
      (set) => ({
        ...DEFAULT_MIX_VALUES,
        setBoardEnabled: (slug: string, enabled: boolean) =>
          set((state) => ({
            boardSettings: updateBoardSetting(state.boardSettings, slug, {
              enabled,
            }),
          })),
        setBoardShare: (slug: string, share: MixShare) =>
          set((state) => ({
            boardSettings: updateBoardSetting(state.boardSettings, slug, {
              share,
            }),
          })),
        resetBoardSettings: () => set({ ...DEFAULT_MIX_VALUES }),
      }),
      {
        name: "nijiurachan-mix-settings",
        version: MIX_STORE_VERSION,
        ...(jsonStorage ? { storage: jsonStorage } : {}),
        migrate: migrateMixState,
        merge: mergeMixState,
        partialize: (state: MixState): MixValues => ({
          boardSettings: state.boardSettings,
        }),
      },
    ),
  );
}

export const useMixStore: MixStore = createMixStore();

/** 二次元裏のslug変更(moge→aimg)前の永続キー。 */
const LEGACY_BOARD_SLUG = "moge";
/** 「メイン」表現を廃止する前の取り分値。 */
const LEGACY_UNCAPPED_SHARE = "main";

/**
 * v1の {enabled, pct} を {enabled, share} へ、v2の moge/"main" を
 * aimg/"uncapped" へ引き継ぐ。
 */
export function migrateMixState(persisted: unknown, version: number): unknown {
  if (
    version >= MIX_STORE_VERSION ||
    typeof persisted !== "object" ||
    persisted === null
  ) {
    return persisted;
  }
  const boardSettings = (persisted as Record<string, unknown>).boardSettings;
  if (typeof boardSettings !== "object" || boardSettings === null) {
    return persisted;
  }
  const migrated: Record<string, unknown> = {};
  for (const [slug, setting] of Object.entries(boardSettings)) {
    if (typeof setting !== "object" || setting === null) continue;
    const record = setting as Record<string, unknown>;
    // v1は取り分をpctで持っていた。v2以降はshare。
    const share = version < 2 ? record.pct : record.share;
    migrated[slug === LEGACY_BOARD_SLUG ? "aimg" : slug] = {
      enabled: record.enabled,
      share: share === LEGACY_UNCAPPED_SHARE ? "uncapped" : share,
    };
  }
  return { ...persisted, boardSettings: migrated };
}

export function mergeMixState(persisted: unknown, current: MixState): MixState {
  if (typeof persisted !== "object" || persisted === null) return current;
  return {
    ...current,
    boardSettings: normalizePersistedBoardSettings(
      (persisted as Record<string, unknown>).boardSettings,
    ),
  };
}

/** localStorageの手編集・旧形式が混ざっても有効な設定だけを残す。 */
function normalizePersistedBoardSettings(value: unknown): MixBoardSettings {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return {};
  }
  const normalized: Record<string, MixBoardSetting> = {};
  for (const [slug, setting] of Object.entries(value)) {
    if (typeof setting !== "object" || setting === null) continue;
    const record = setting as Record<string, unknown>;
    const share =
      record.share === "uncapped"
        ? ("uncapped" as const)
        : MIX_SHARE_PERCENTS.find((candidate) => candidate === record.share);
    if (typeof record.enabled !== "boolean" || share === undefined) continue;
    normalized[slug] = Object.freeze({ enabled: record.enabled, share });
  }
  return normalized;
}

function resolveMixStateStorage(
  storage: StateStorage | undefined,
): StateStorage | undefined {
  if (storage) return storage;
  try {
    return globalThis.localStorage ?? undefined;
  } catch {
    return undefined;
  }
}
